import { animated, config, useSpring } from '@react-spring/web';
import useComponentSize from '@rehooks/component-size';
import { ReactNode, useRef } from 'react';

import { Box } from '../../common/ui/Box';

import { CollapsableContent } from './components/CollapsableContent';
import { CollapsableDescription } from './components/CollapsableDescription';
import { CollapsableHeader } from './components/CollapsableHeader';
import { CollapsableTitle } from './components/CollapsableTitle';

interface CollapsableProps {
    isOpen: boolean;
    children?: ReactNode | (() => ReactNode);
    header: ReactNode;
}

export const Collapsable = (props: CollapsableProps) => {
    const collapsableBodyRef = useRef<HTMLDivElement>(null);
    const { height } = useComponentSize(collapsableBodyRef);

    const closedState = {
        opacity: 0,
        height: 0,
        borderBottomWidth: 0,
    };

    const openState = {
        opacity: 1,
        height,
        borderBottomWidth: 0,
    };

    const contentAnimation = useSpring({
        ...(props.isOpen ? openState : closedState),
        config: {
            ...config.default,
            tension: 300,
            clamp: true,
        },
    });

    let dynamicChildren: ReactNode;

    if (typeof props.children === 'function') {
        dynamicChildren = props.children();
    } else {
        dynamicChildren = props.children;
    }

    return (
        <div>
            {props.header}
            <animated.div
                aria-hidden={!props.isOpen}
                inert={!props.isOpen}
                style={{
                    overflow: 'hidden',
                    ...contentAnimation,
                }}
            >
                <Box ref={collapsableBodyRef}>{dynamicChildren}</Box>
            </animated.div>
        </div>
    );
};

Collapsable.Header = CollapsableHeader;
Collapsable.Content = CollapsableContent;
Collapsable.Title = CollapsableTitle;
Collapsable.Description = CollapsableDescription;
